<script setup lang="ts">
import Modal from "@/components/Base/Modal.vue";
import TipTapEditor from "@/components/TipTapEditor/index.vue";
import {
  SubjectTypeEnum,
  SchoolTypeEnum,
  GradeLevelEnum,
} from "@/services/fobizzToolsApi";
import type { App, Item } from "@/services/fobizzToolsApi";
import { useDialog } from "@/utils/dialog";
import { RouteLocationRaw } from "vue-router";

const { t } = useI18n();

const { openConfirm } = useDialog();

const router = useRouter();

type Props = {
  open: boolean;
  htmlContent: string;
  isAuthor: boolean;
  item: Item;
  app?: App;
  editCategoriesLink?: RouteLocationRaw;
};

const props = withDefaults(defineProps<Props>(), {
  open: true,
  isAuthor: false,
  app: undefined,
  editCategoriesLink: undefined,
});

const emit = defineEmits(["hide", "hidden", "update", "focus"]);

const defaultModalContent = computed(() => {
  return t(`infoModal.${props.app?.namespace}`);
});

const htmlContent = ref(props.htmlContent || defaultModalContent.value);

const contentChangedFromDefault = computed(
  () => defaultModalContent.value !== htmlContent.value
);

const isSubmittingDone = ref(false);
const hasEditorContentChanged = ref(false);

const handleEditorChanged = () => {
  hasEditorContentChanged.value = true;
};

const schoolTypeTags = computed(() => {
  if (!props.item?.schoolTypes) {
    return [];
  }
  const selectedSchoolTypes = props.item.schoolTypes.filter((x) => x !== null);
  return selectedSchoolTypes?.map((type) => {
    return t(`enums.schoolTypes.${SchoolTypeEnum[type]}`);
  });
});

const gradeLevelTags = computed(() => {
  if (!props.item?.gradeLevels) {
    return [];
  }
  const selectedGradeLevels = props.item.gradeLevels.filter((x) => x !== null);
  return selectedGradeLevels?.map((level) => {
    return t(`enums.gradeLevels.${GradeLevelEnum[level]}`);
  });
});

const subjectTypeTags = computed(() => {
  if (!props.item?.subjectTypes) {
    return [];
  }
  const selectedSubjectTypes = props.item.subjectTypes.filter(
    (x) => x !== null
  );
  return selectedSubjectTypes?.map((type) => {
    return t(`enums.subjectTypes.${SubjectTypeEnum[type]}`);
  });
});

const hasTags = computed<boolean>(() => {
  return !!(
    schoolTypeTags.value?.length ||
    gradeLevelTags.value?.length ||
    subjectTypeTags.value?.length
  );
});

const handleSave = () => {
  emit("update", htmlContent.value);
  isSubmittingDone.value = true;
  handleHide();
};

const enableConfirmHide = computed(() => {
  if (isSubmittingDone.value) {
    return false;
  }
  if (hasEditorContentChanged.value && props.isAuthor) {
    return true;
  }
  return false;
});

const confirmHideFunc = async () => {
  return await openConfirm(
    t("confirms.discardChanges.text"),
    t("confirms.discardChanges.confirm")
  );
};

const confirmHideFuncWithRedirect = async () => {
  const result = await openConfirm(
    t("confirms.discardChanges.text"),
    t("confirms.discardChanges.confirm")
  );
  if (result && props.editCategoriesLink) {
    router.push(props.editCategoriesLink);
  }
  return result;
};

const handleHide = () => {
  emit("hide");
  hasEditorContentChanged.value = false;
};

const handleHidden = () => emit("hidden");

const handleEditCategoriesLink = () => {
  if (enableConfirmHide.value) {
    confirmHideFuncWithRedirect();
  } else if (props.editCategoriesLink) {
    router.push(props.editCategoriesLink);
  }
};
</script>

<template>
  <teleport to="body">
    <Modal
      size="xl"
      class="h-100"
      :class="{ 'info-modal--no-border': !isAuthor }"
      :open="open"
      :confirm-hide-func="confirmHideFunc"
      :enable-confirm-hide="enableConfirmHide"
      :focus="false"
      @hide="handleHide"
      @hidden="handleHidden"
    >
      <div class="modal-header info-modal__modal-header">
        <h1 class="modal-title info-modal__modal-title">
          {{ t("infoModal.title") }}
        </h1>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>
      <div class="modal-body">
        <div
          v-if="hasTags || isAuthor"
          class="d-flex align-items-start justify-content-between mb-5"
        >
          <div class="categories categories--info-modal">
            <div
              v-if="schoolTypeTags.length !== 0 || isAuthor"
              class="category"
            >
              <div class="category__title text-muted">
                {{ t("labels.schoolTypes") }}
              </div>
              <div class="category__tags">
                <em v-if="schoolTypeTags.length == 0 && isAuthor">{{
                  t("hints.notSpecified")
                }}</em>
                <span
                  v-for="tag in schoolTypeTags"
                  :key="tag"
                  class="material-tile__tag category__tag"
                  >{{ tag }}</span
                >
              </div>
            </div>
            <div
              v-if="gradeLevelTags.length !== 0 || isAuthor"
              class="category"
            >
              <div class="category__title text-muted">
                {{ t("labels.gradeLevels") }}
              </div>
              <div class="category__tags">
                <em v-if="gradeLevelTags.length == 0 && isAuthor">{{
                  t("hints.notSpecified")
                }}</em>
                <span
                  v-for="tag in gradeLevelTags"
                  :key="tag"
                  class="material-tile__tag category__tag"
                  >{{ tag }}</span
                >
              </div>
            </div>
            <div
              v-if="subjectTypeTags.length !== 0 || isAuthor"
              class="category"
            >
              <div class="category__title text-muted">
                {{ t("labels.subjectTypes") }}
              </div>
              <div class="category__tags">
                <em v-if="subjectTypeTags.length == 0 && isAuthor">{{
                  t("hints.notSpecified")
                }}</em>
                <span
                  v-for="tag in subjectTypeTags"
                  :key="tag"
                  class="material-tile__tag category__tag"
                  >{{ tag }}</span
                >
              </div>
            </div>
          </div>
          <button
            v-if="isAuthor"
            class="ms-5 py-0 btn btn-link"
            @click="handleEditCategoriesLink"
          >
            <i class="bi bi-pencil"></i>
          </button>
        </div>
        <TipTapEditor
          v-if="isAuthor"
          v-model="htmlContent"
          toolbar-mode="lg"
          :full-height="true"
          :show-toolbar="isAuthor"
          :readonly-editor="!isAuthor"
          :parent-id="item.id"
          :parent-type="item.kind"
          @focus="emit('focus')"
          @update:model-value="handleEditorChanged"
        />
        <ProsaHtml v-else-if="contentChangedFromDefault" :text="htmlContent" />
      </div>
      <div class="modal-footer">
        <button
          v-if="isAuthor"
          class="btn btn-primary btn-rounded"
          @click="handleSave"
        >
          {{ t("buttons.save") }}
        </button>
        <button v-else class="btn btn-primary btn-rounded" @click="handleHide">
          {{ t("buttons.close") }}
        </button>
      </div>
    </Modal>
  </teleport>
</template>

<style scoped lang="scss">
.info-modal {
  &--no-border {
    .ProseMirror {
      border: none;
      padding: 0;
    }
  }

  &__modal-header {
    background-color: $light;
    margin-bottom: $spacer-3;
  }

  &__modal-title {
    font-size: $h5-font-size;
  }
}

.categories {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  margin-bottom: $spacer-5;

  @include media-breakpoint-down(md) {
    grid-template-columns: 1fr;
    margin-bottom: $spacer-4;
    padding-right: 0;
  }

  &__title {
    font-size: $h4-font-size;
    margin-bottom: $spacer-3;

    @include media-breakpoint-down(md) {
      font-size: $h5-font-size;
      margin-bottom: $spacer-2;
    }
  }

  &--info-modal {
    margin-bottom: 0;
  }
}

.category {
  display: flex;
  gap: $spacer-1;
  font-size: $h6-font-size;

  &:not(:last-of-type) {
    margin-right: $spacer-4;
  }

  @include media-breakpoint-down(md) {
    display: grid;
    grid-template-columns: 100px auto;
    max-width: 100%;
    margin-bottom: $spacer-3;
  }

  &__title {
    height: 100%;
    margin-right: $spacer-1;
    font-size: $font-size-base;

    @include media-breakpoint-down(md) {
      font-size: $font-size-sm;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    height: fit-content;
    gap: $spacer-2;
  }

  &__tag {
    display: flex;
    width: fit-content;
    height: fit-content;
    font-weight: $font-weight-bold;
    color: $color-black;
  }
}
</style>
